/**
 * Slovenian copy of the /docs page — every user-visible string in one place.
 * The page never renders server text except entry content (titles,
 * descriptions, notes), which is authored in Slovenian (and Czech for the
 * ČÚZK entries) in sxwiki_entries.
 *
 * `DocsCopy` is THIS object's shape: the Czech set (docsCopyCs.ts, for /cz/docs
 * since 2026-09-28) must match it key for key and function for function — a
 * missing string fails the build. The live `docsCopy` binding (docsCopy.ts)
 * points at one of the two per page load; every renderer reads it at call time.
 */

import type { WikiCountry } from '../../shared/types/layerWiki.js';
import { BRAND } from '../../shared/utils/brand.js';

/** Slovenian count word by `n mod 100` (1 / 2 / 3–4 / 5+). */
function plural(n: number, one: string, two: string, few: string, many: string): string {
  const m100 = n % 100;
  if (m100 === 1) return one;
  if (m100 === 2) return two;
  if (m100 === 3 || m100 === 4) return few;
  return many;
}

/** Slovenian plural of "sloj" (1 sloj, 2 sloja, 3–4 sloji, 5+ slojev). */
function slojev(n: number): string {
  return `${n} ${plural(n, 'sloj', 'sloja', 'sloji', 'slojev')}`;
}

/** Slovenian plural of "izdajatelj". */
function izdajateljev(n: number): string {
  return `${n} ${plural(n, 'izdajatelj', 'izdajatelja', 'izdajatelji', 'izdajateljev')}`;
}

/** Slovenian plural of "mapa" (1 mapa, 2 mapi, 3–4 mape, 5+ map). */
function map(n: number): string {
  return `${n} ${plural(n, 'mapa', 'mapi', 'mape', 'map')}`;
}

/** Locative after »v«: v 1 mapi, v 2 mapah, v 3+ mapah. */
function mapah(n: number): string {
  return `${n} ${n % 100 === 1 ? 'mapi' : 'mapah'}`;
}

/** Slovenian plural of "tema" (1 tema, 2 temi, 3–4 teme, 5+ tem). */
function tem(n: number): string {
  return `${n} ${plural(n, 'tema', 'temi', 'teme', 'tem')}`;
}

export const docsCopySl = {
  /** Whose words the description is (`sxwiki_entries.description_origin`). */
  origin: {
    imps: 'INSPIRE metapodatkovni zapis',
    opsi: 'OPSI (podatki.gov.si)',
    publisher_page: 'spletna stran vira',
    cuzk_metadata: 'metapodatki ČÚZK',
    osm_wiki: 'OSM wiki',
    sxlayer_desc: 'opis sloja v KingCAD',
    king: BRAND.name,
  } as Record<string, string>,

  /** "{n} slojev" with the Slovenian plural. */
  count: (n: number): string => slojev(n),
  /** "{n} izdajateljev" with the Slovenian plural. */
  publishers: (n: number): string => izdajateljev(n),
  /**
   * The chip's age wording (Teo 2026-09-08: "Posodobljeno n dni nazaj",
   * "m mesecev nazaj"): accusative after »nazaj«, Slovenian dual on n mod 100.
   */
  age: {
    today: 'danes',
    days: (n: number): string => `${n} ${n % 100 === 1 ? 'dan' : 'dni'} nazaj`,
    months: (n: number): string => `${n} ${plural(n, 'mesec', 'meseca', 'mesece', 'mesecev')} nazaj`,
    years: (n: number): string => `${n} ${plural(n, 'leto', 'leti', 'leta', 'let')} nazaj`,
  },
  /** The chip without a known date. */
  updated: 'Posodobljeno',
  updatedAgo: (age: string): string => `Posodobljeno ${age}`,

  searchPlaceholder: 'Išči sloj ali vir …',
  searchLabel: 'Išči sloj ali vir',

  /**
   * The two product sections — »King.Prostor Web« and »King.Prostor CAD« (Teo 2026-09-16;
   * »Splet« / »KingCAD« until then). KingCAD stays the desktop product's own name in prose.
   */
  sections: { web: `${BRAND.name} Web`, cad: `${BRAND.name} CAD` },
  /** Two sections since 0584 (Teo 2026-09-14): the web catalogue and the KingCAD tree. */
  tabs: { pregled: 'Pregled', katalog: `${BRAND.name} Web`, cad: `${BRAND.name} CAD`, viri: 'Viri in licence' },
  tabsLabel: 'Razdelki dokumentacije',

  header: {
    suffix: '/ Dokumentacija',
    homeLabel: 'Dokumentacija slojev — domov',
    testBadge: 'Testna izdaja',
    lang: 'SL',
    /** The brand's own site, never the legal entity (brand.ts). */
    siteLink: `${BRAND.siteHost} ↗`,
    /** The SI · CZ switch (2026-09-28): its aria-label and each half's full name (link titles). */
    countries: 'Podatki po državah',
    countryNames: { si: 'Slovenija', cz: 'Češka' } as Record<WikiCountry, string>,
    /** #875: the way back to the app the reader came from. */
    appLinks: 'Nazaj v aplikacijo',
    mapLink: 'Zemljevid',
    homeLink: 'Začetna stran',
  },

  hero: {
    eyebrow: 'Viri, licence in stanje podatkov',
    title: `${BRAND.name}. Sloj za slojem.`,
    lead: `Za vsak sloj, ki ga ${BRAND.name} vzdržuje: kaj je z besedami izdajatelja, kdo ga objavlja in pod katero licenco, kako pogosto ga osvežujemo ter kdaj sta spletni zemljevid in KingCAD začela streči trenutne podatke.`,
    cta: 'Preglejte katalog slojev →',
    viriLink: 'Viri in licence →',
  },
  stats: { layers: `slojev v ${BRAND.name} Web`, cadLayers: `slojev v ${BRAND.name} CAD`, sources: 'virov', themes: 'tem' },
  next: {
    heading: 'Kako želite nadaljevati?',
    katalog: {
      eyebrow: 'Preverite',
      title: `${BRAND.name} Web`,
      text: (nLayers: number): string => `Vseh ${slojev(nLayers)} po temah — opis, datumi, stanje in kje sloj živi. →`,
    },
    cad: {
      eyebrow: 'Prebrskajte',
      title: `${BRAND.name} CAD`,
      text: (nLayers: number, nFolders: number): string =>
        `Drevo slojev, kot ga prikaže KingCAD — ${slojev(nLayers)} v ${mapah(nFolders)}. →`,
    },
    viri: {
      eyebrow: 'Razumite',
      title: 'Viri in licence',
      text: (nSources: number): string =>
        `${izdajateljev(nSources)}, njihove licence in obvezne navedbe vira. →`,
    },
  },

  viri: {
    eyebrow: 'Dokumentacija slojev',
    title: 'Viri in licence',
    intro: (nSources: number, nOwnLicence: number): string => {
      const carries = plural(nOwnLicence, 'nosi', 'nosita', 'nosijo', 'nosi');
      return `${izdajateljev(nSources)}; navedba vira je obvezni pripis, ki ga zemljevid izpiše ob uporabi sloja. ${slojev(nOwnLicence)} ${carries} svojo licenco, ki prevlada nad licenco vira.`;
    },
    licencePerEntry: 'Licenca po posameznem sloju',
    attribution: 'Navedba',
    sourceSite: 'Spletna stran vira ↗',
    /** The jump list over the publisher sections. */
    tocLabel: 'Izdajatelji',
  },

  /**
   * The King.Prostor Web section (2026-09-28, Teo: "1 group is 1 page, with listed entries
   * for each layer … same ui/ux for web as for cad page"): the card landing, the theme pages
   * and the page-list nav — the same shape as the KingCAD section below.
   */
  web: {
    title: `${BRAND.name} Web`,
    crumb: `${BRAND.name} Web`,
    /** The landing's one line under the title: »14 tem · 75 slojev«. */
    intro: (nThemes: number, nLayers: number): string => `${tem(nThemes)} · ${slojev(nLayers)}`,
    navLabel: `Sloji ${BRAND.name} Web`,
    navToggle: (n: number): string => `Sloji ${BRAND.name} Web · ${n}`,
    /** The theme pages. */
    page: {
      /** The rail title on a theme page — the entries listed on it. */
      onThisPage: 'Sloji',
      /** `…/docs/web/<slug>` names no theme. */
      notFound: 'Za to temo ni zapisa v dokumentaciji.',
      notFoundLink: `Na pregled ${BRAND.name} Web →`,
    },
  },

  /** The KingCAD section (0584): the card landing, the folder pages and the tree nav. */
  cad: {
    title: `${BRAND.name} CAD`,
    crumb: `${BRAND.name} CAD`,
    /** The landing's one line under the title: »61 map · 420 slojev«. */
    intro: (nPages: number, nLayers: number): string => `${map(nPages)} · ${slojev(nLayers)}`,
    navLabel: `Sloji ${BRAND.name} CAD`,
    navToggle: (n: number): string => `Sloji ${BRAND.name} CAD · ${n}`,
    /** aria-label of a folder's disclosure button (the state rides in aria-expanded). */
    folderToggle: (name: string): string => `Mapa ${name}`,
    rastriFolder: 'Rastri in LiDAR',
    layersIn: (n: number): string => slojev(n),
    /** A layer the map names that has no wiki entry yet (greyed section / search row). */
    noEntry: 'Sloj še nima zapisa v dokumentaciji.',
    /** The included map is not registered on this database. */
    missingFolder: 'Zemljevid te mape ni registriran.',
    unavailable: 'Drevo slojev KingCAD trenutno ni na voljo.',
    /**
     * The folder pages (Teo 2026-09-16: "each lowest level folder [has] a page, with layers
     * all on 1 page listed"): one page per folder that holds layers, the layers as sections.
     */
    folder: {
      /** The rail title on a folder page — the layers listed on it. */
      onThisPage: 'Sloji',
      /** Folder pages nested under this one (a folder that holds layers AND sub-folders). */
      subfolders: 'Podmape',
      /** `…/docs/cad/<slugs>` names no folder of the tree. */
      notFound: 'Za to mapo ni zapisa v dokumentaciji.',
      notFoundLink: `Na pregled ${BRAND.name} CAD →`,
    },
  },
  /** An entry whose description is still empty (the KingCAD layers without a desktop text). */
  noDescription: 'Opis še ni napisan.',

  katalog: {
    crumbRoot: 'Dokumentacija',
    /** The OTHER language's title / text block (2026-09-28): Czech on /si/docs, Slovenian on /cz/docs. */
    altTitle: 'česky',
    altHeading: 'Česky',
    prev: '← Prejšnji',
    next: 'Naslednji →',
    onThisPage: 'Na tej strani',
    permalink: 'Trajna povezava:',
    rail: { opis: 'Opis', podatki: 'Podatki', povezave: 'Povezave', povezani: 'Povezani sloji', sestavni: 'Sestavni deli', zgodovina: 'Zgodovina urejanja' },
    /** The unknown-key notice's way out. */
    notFoundLink: `Na pregled ${BRAND.name} Web →`,
    /** Tooltip suffix on an indented row whose parent sits in another theme. */
    partOf: (parentTitle: string): string => `del: ${parentTitle}`,
  },

  facts: {
    /** ONE row: "<publisher>, <credit line> (<licence>)" (Teo 2026-09-28; three rows before). */
    source: 'Vir',
    vintage: 'Datum vira',
    /** Acquisition years of a DOF flight / LiDAR scan. */
    acquisition: 'Leto snemanja',
    /** Rendered uppercase by the facts card: »DATUM KING.PROSTOR WEB« (Teo 2026-09-28; »King.Prostor web« before). */
    web: `Datum ${BRAND.name} web`,
    /** »DATUM KING.PROSTOR CAD« (Teo 2026-09-28; »KingCAD« before). */
    desktop: `Datum ${BRAND.name} CAD`,
    /** Whose words the description is — the note beside »Uredi opis« (admins only since 2026-09-28). */
    origin: 'Besedilo',
    /** Appended to »Besedilo« when a king admin edited the text on /docs: "· urejeno 2026-09-08". */
    edited: (date: string): string => `urejeno ${date}`,
    /** A date row the import ledgers cannot fill (Teo 2026-09-15: the row stays, blank). */
    unknownDate: '—',
    unknownDateTitle: 'Datum ni zabeležen v evidenci uvozov',
  },

  /** King-admin text editing (0569) — the »Uredi opis« form and the history section. */
  edit: {
    button: 'Uredi opis',
    title: 'Urejanje opisa',
    sl: 'Opis (slovensko)',
    cs: 'Opis (česky) — neobvezno',
    note: 'Opomba k spremembi (neobvezno)',
    hint: 'Prazna vrstica začne nov odstavek. Brez oblikovanja in povezav.',
    save: 'Shrani',
    saving: 'Shranjujem …',
    cancel: 'Prekliči',
    history: 'Zgodovina urejanja',
    noHistory: 'Besedilo še ni bilo ročno urejeno.',
    historyFailed: 'Zgodovine urejanja ni bilo mogoče naložiti.',
    previous: 'Prejšnje besedilo',
    restore: 'Obnovi',
    restoreConfirm: (when: string): string => `Obnovim besedilo, kakršno je bilo pred spremembo z dne ${when}?`,
    actions: { text_update: 'urejanje', text_restore: 'obnova' } as Record<'text_update' | 'text_restore', string>,
    errors: {
      emptySl: 'Slovenski opis ne sme biti prazen.',
      noChange: 'Ni sprememb za shranjevanje.',
      forbidden: 'Za urejanje se prijavite v brskalniku kot skrbnik.',
      notFound: 'Ta zapis ne obstaja več.',
      generic: 'Shranjevanje ni uspelo. Poskusite znova.',
    },
  },
  links: {
    publisher: 'Spletna stran vira',
    metadata: 'Metapodatki (INSPIRE / OPSI)',
    heading: 'Povezave',
  },
  componentsHeading: 'Sestavni deli',
  /** Links between entries (parentKey), by relation kind — the child's line and the parent's groups. */
  related: {
    heading: 'Povezani sloji',
    parent: { part: 'Del sloja', computed: 'Izračunano iz sloja', preset: 'Posebni pogled na sloj', cad: 'Podatkovni sloj' },
    children: { part: 'Podrejeni sloji', computed: 'Izpeljani sloji', preset: 'Posebni pogledi', cad: `${BRAND.name} CAD sloji` },
  },
  /** Sheet / tile count of a catalog product: "3.304 listi". */
  sheets: (n: number): string => {
    const word = plural(n, 'list', 'lista', 'listi', 'listov');
    return `${String(n).replace(/\B(?=(\d{3})+(?!\d))/g, '.')} ${word}`;
  },
  parked: 'ustavljeno',

  /** Tile-state qualifiers appended to the »King.Prostor web« fact. */
  tilesBehind: 'starejše od uvoza',
  tilesLive: 'v živo',
  /** Title (hover text) of the »King.Prostor web« fact: "Datum ploščic: <date>". */
  tilesDated: 'Datum ploščic',
  /**
   * The surface chip: a web-tab entry KingCAD does not serve. There is no desktop
   * counterpart — an entry only KingCAD serves reads under the CAD tab, which says it
   * (Teo 2026-09-16: »Samo KingCAD« was dead by then).
   */
  surface: { web: 'Samo splet' },

  titles: {
    base: `Dokumentacija slojev – ${BRAND.name}`,
    viri: `Viri in licence – Dokumentacija slojev – ${BRAND.name}`,
    web: `${BRAND.name} Web – Dokumentacija slojev – ${BRAND.name}`,
    webPage: (theme: string): string => `${theme} – ${BRAND.name} Web – Dokumentacija slojev – ${BRAND.name}`,
    cad: `${BRAND.name} CAD – Dokumentacija slojev – ${BRAND.name}`,
    cadFolder: (name: string): string => `${name} – ${BRAND.name} CAD – Dokumentacija slojev – ${BRAND.name}`,
    entry: (title: string): string => `${title} – Dokumentacija slojev – ${BRAND.name}`,
    notFound: `Ni zapisa – Dokumentacija slojev – ${BRAND.name}`,
  },

  error: 'Dokumentacija slojev trenutno ni na voljo.',
  retry: 'Poskusi znova',
  emptySearch: (q: string): string => `Ni slojev za »${q}«.`,
  clearSearch: 'Počisti iskanje',
  noEntries: 'Dokumentacija še nima vnosov.',
  notFound: 'Za ta sloj še ni zapisa v dokumentaciji.',
  loading: 'Nalagam …',
};

/** The shape every language set must fill. */
export type DocsCopy = typeof docsCopySl;
